/**
 * ClauseGuard Frontend
 * Landing page with analysis functionality
 */

import { useState, useCallback, useEffect } from 'react';
import type { ApiResponse, Analysis, Clause, Plan } from '../types/api';
import {
  trackPageView,
  trackAnalysisStarted,
  trackAnalysisCompleted,
  trackAnalysisFailed,
  trackCheckoutStarted,
  trackCheckoutCompleted,
} from './analytics';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// ============================================================================
// Pricing Configuration
// ============================================================================

interface PricingTier {
  name: string;
  plan: Plan;
  price: number;
  period: string;
  analyses: number;
  features: string[];
  highlighted?: boolean;
  cta: string;
}

const PRICING_TIERS: PricingTier[] = [
  {
    name: 'Starter',
    plan: 'starter',
    price: 9,
    period: 'month',
    analyses: 5,
    features: [
      '5 contract analyses/month',
      'Basic risk identification',
      'Email support',
      'DOCX & PDF support',
    ],
    cta: 'Get Started',
  },
  {
    name: 'Professional',
    plan: 'professional',
    price: 29,
    period: 'month',
    analyses: 25,
    features: [
      '25 contract analyses/month',
      'Advanced risk analysis',
      'Negotiation suggestions',
      'Priority email support',
      'Analysis history',
    ],
    highlighted: true,
    cta: 'Start Free Trial',
  },
  {
    name: 'Business',
    plan: 'business',
    price: 79,
    period: 'month',
    analyses: 100,
    features: [
      '100 contract analyses/month',
      'Team collaboration',
      'Custom clause detection',
      'Priority support',
      'API access',
      'Bulk analysis',
    ],
    cta: 'Contact Sales',
  },
];

// ============================================================================
// API Functions
// ============================================================================

async function analyzeFile(file: File, token: string, context?: string): Promise<Analysis> {
  const formData = new FormData();
  formData.append('file', file);
  if (context) {
    formData.append('context', context);
  }

  const response = await fetch(`${API_BASE}/api/analyze`, {
    method: 'POST',
    body: formData,
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  const result: ApiResponse<Analysis> = await response.json();

  if (!result.success || !result.data) {
    throw new Error(result.error?.message || 'Analysis failed');
  }

  return result.data;
}

async function createCheckoutSession(plan: Plan): Promise<string> {
  const response = await fetch(`${API_BASE}/api/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan }),
  });

  const result = await response.json() as { success: boolean; url?: string; error?: { message?: string } };

  if (!result.success) {
    throw new Error(result.error?.message || 'Checkout failed');
  }

  return result.url || '';
}

// ============================================================================
// Main App Component
// ============================================================================

type View = 'landing' | 'dashboard' | 'privacy' | 'terms' | 'success' | 'verify-email';

export default function App() {
  const [view, setView] = useState<View>('landing');
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [checkingOut, setCheckingOut] = useState<Plan | null>(null);

  // Check for existing token on mount
  useEffect(() => {
    const stored = localStorage.getItem('clauseguard_token');
    if (stored) {
      setToken(stored);
    }

    // Check for success redirect from Stripe checkout
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      // Clear the URL params and show success page
      window.history.replaceState({}, '', window.location.pathname);
      setView('success');
      trackCheckoutCompleted('unknown'); // We don't know which plan was purchased
    }

    // Check for email verification token
    const verifyToken = params.get('token');
    const isVerifyPath = window.location.pathname === '/verify-email' || params.get('verify') === 'true';
    if (isVerifyPath) {
      // Navigate to verify-email page regardless of whether token exists
      // The page will handle the "no token" error case
      setView('verify-email');
    }

    // Track initial page view
    trackPageView('landing');
  }, []);

  const handleFile = useCallback(async (file: File) => {
    if (!token) {
      setError('Please sign in to analyze contracts');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysis(null);
    setView('dashboard');

    // Track analysis started
    trackAnalysisStarted(file.name, file.size);

    try {
      const result = await analyzeFile(file, token);
      setAnalysis(result);

      // Track analysis completed
      if (result.summary) {
        trackAnalysisCompleted(
          result.summary.total_clauses,
          result.summary.high_risk_count,
          result.summary.medium_risk_count,
          result.summary.low_risk_count
        );
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      trackAnalysisFailed(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handlePricingCta = useCallback(async (tier: PricingTier) => {
    if (!token) {
      // Dev mode: generate a dev token
      const devToken = `dev-token-${Date.now()}`;
      localStorage.setItem('clauseguard_token', devToken);
      setToken(devToken);
    }

    setCheckingOut(tier.plan);

    // Track checkout started
    trackCheckoutStarted(tier.plan);

    try {
      const url = await createCheckoutSession(tier.plan);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
      setCheckingOut(null);
    }
  }, [token]);

  const scrollToPricing = useCallback(() => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const scrollToDemo = useCallback(() => {
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div style={styles.container}>
      {view === 'landing' ? (
        <LandingPage
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onFileChange={handleInputChange}
          dragActive={dragActive}
          loading={loading}
          error={error}
          onScrollToPricing={scrollToPricing}
          onScrollToDemo={scrollToDemo}
          onPricingCta={handlePricingCta}
          checkingOut={checkingOut}
          onNavigate={setView}
        />
      ) : view === 'dashboard' ? (
        <Dashboard
          analysis={analysis}
          loading={loading}
          error={error}
          dragActive={dragActive}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onFileChange={handleInputChange}
          onBack={() => setView('landing')}
          onErrorDismiss={() => setError(null)}
        />
      ) : view === 'success' ? (
        <SuccessPage
          onBack={() => setView('landing')}
          onAnalyze={() => setView('landing')}
        />
      ) : view === 'privacy' ? (
        <LegalPage
          title="Privacy Policy"
          onBack={() => setView('landing')}
        >
          <PrivacyPolicyContent />
        </LegalPage>
      ) : view === 'terms' ? (
        <LegalPage
          title="Terms of Service"
          onBack={() => setView('landing')}
        >
          <TermsOfServiceContent />
        </LegalPage>
      ) : view === 'verify-email' ? (
        <VerifyEmailPage
          onBack={() => setView('landing')}
          onSuccess={() => setView('landing')}
        />
      ) : null}
    </div>
  );
}

// ============================================================================
// Landing Page Component
// ============================================================================

interface LandingPageProps {
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  dragActive: boolean;
  loading: boolean;
  error: string | null;
  onScrollToPricing: () => void;
  onScrollToDemo: () => void;
  onPricingCta: (tier: PricingTier) => void;
  checkingOut: Plan | null;
  onNavigate: (view: View) => void;
}

function LandingPage({
  onDrop,
  onDragOver,
  onDragLeave,
  onFileChange,
  dragActive,
  loading,
  error,
  onScrollToPricing,
  onScrollToDemo,
  onPricingCta,
  checkingOut,
  onNavigate,
}: LandingPageProps) {
  return (
    <>
      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navContent}>
          <div style={styles.logo}>
            <svg style={styles.logoIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            <span style={styles.logoText}>ClauseGuard</span>
          </div>
          <div style={styles.navLinks}>
            <a href="#features" style={styles.navLink}>Features</a>
            <a href="#pricing" style={styles.navLink}>Pricing</a>
            <a href="#faq" style={styles.navLink}>FAQ</a>
            <button style={styles.navCta} onClick={onScrollToDemo}>Try Free</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>
            Don't Sign Your Rights Away
          </h1>
          <p style={styles.heroSubtitle}>
            AI-powered contract analysis that spots hidden risks in minutes, not hours.
            Protect yourself from unfair clauses before you sign.
          </p>
          <div style={styles.heroCtas}>
            <button style={styles.primaryCta} onClick={onScrollToDemo}>
              Analyze Your Contract Free
            </button>
            <button style={styles.secondaryCta} onClick={onScrollToPricing}>
              View Pricing
            </button>
          </div>
          <div style={styles.trustBadges}>
            <span style={styles.trustBadge}>No credit card required</span>
            <span style={styles.trustDivider}>|</span>
            <span style={styles.trustBadge}>Results in 30 seconds</span>
            <span style={styles.trustDivider}>|</span>
            <span style={styles.trustBadge}>10,000+ contracts analyzed</span>
          </div>
        </div>
        <div style={styles.heroVisual}>
          <div style={styles.mockAnalysisCard}>
            <div style={styles.mockHeader}>
              <span style={styles.mockTitle}>Risk Analysis</span>
              <span style={styles.mockRisk}>3 Issues Found</span>
            </div>
            <div style={styles.mockClauses}>
              <div style={{ ...styles.mockClause, borderColor: '#dc2626' }}>
                <span>Non-compete clause detected</span>
                <span style={styles.mockHigh}>HIGH RISK</span>
              </div>
              <div style={{ ...styles.mockClause, borderColor: '#f59e0b' }}>
                <span>Unlimited liability</span>
                <span style={styles.mockMedium}>MEDIUM</span>
              </div>
              <div style={{ ...styles.mockClause, borderColor: '#f59e0b' }}>
                <span>IP assignment ambiguity</span>
                <span style={styles.mockMedium}>MEDIUM</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" style={styles.demoSection}>
        <h2 style={styles.sectionTitle}>Try It Now</h2>
        <p style={styles.sectionSubtitle}>Upload a contract and see the results instantly</p>

        <div
          style={{ ...styles.dropZone, ...(dragActive ? styles.dropZoneActive : {}) }}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
        >
          <svg style={styles.uploadIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
          </svg>
          <p style={styles.dropText}>Drag and drop your contract here</p>
          <p style={styles.dropSubtext}>or click to browse (PDF, DOCX up to 10MB)</p>
          <input type="file" accept=".pdf,.docx" onChange={onFileChange} style={styles.fileInput} />
        </div>

        {loading && (
          <div style={styles.loadingDemo}>
            <div style={styles.spinner} />
            <p style={styles.loadingText}>Analyzing your contract...</p>
          </div>
        )}

        {error && (
          <div style={styles.errorDemo}>
            <p style={styles.errorText}>{error}</p>
          </div>
        )}
      </section>

      {/* Features Section */}
      <section id="features" style={styles.featuresSection}>
        <h2 style={styles.sectionTitle}>Why Freelancers Trust ClauseGuard</h2>
        <div style={styles.featuresGrid}>
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <h3 style={styles.featureTitle}>30-Second Analysis</h3>
            <p style={styles.featureDesc}>
              Upload your contract and get results in seconds, not hours.
              No more waiting for legal review.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
            </div>
            <h3 style={styles.featureTitle}>Risk Detection</h3>
            <p style={styles.featureDesc}>
              Identifies non-compete clauses, hidden fees, IP grabs, and other
              dangerous provisions that could hurt you later.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h3 style={styles.featureTitle}>Negotiation Tips</h3>
            <p style={styles.featureDesc}>
              Get actionable suggestions on how to negotiate better terms.
              Know what to ask for and why it matters.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3 style={styles.featureTitle}>Private & Secure</h3>
            <p style={styles.featureDesc}>
              Your contracts are processed securely and never stored.
              Your sensitive documents stay private.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
              </svg>
            </div>
            <h3 style={styles.featureTitle}>All Formats</h3>
            <p style={styles.featureDesc}>
              Works with PDF, DOCX, and scanned documents.
              Upload whatever format your client sent you.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20V10M18 20V4M6 20v-4" />
              </svg>
            </div>
            <h3 style={styles.featureTitle}>Track History</h3>
            <p style={styles.featureDesc}>
              Keep a record of all your analyzed contracts.
              Compare different versions and track changes.
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section style={styles.socialProof}>
        <h2 style={styles.sectionTitle}>Trusted by Freelancers</h2>
        <div style={styles.testimonials}>
          <div style={styles.testimonial}>
            <p style={styles.testimonialText}>
              "Caught a hidden auto-renewal clause that would have locked me in for 2 years.
              Worth every penny."
            </p>
            <div style={styles.testimonialAuthor}>
              <span style={styles.authorName}>Sarah M.</span>
              <span style={styles.authorRole}>UX Designer</span>
            </div>
          </div>
          <div style={styles.testimonial}>
            <p style={styles.testimonialText}>
              "I use ClauseGuard for every new client contract. It's saved me from
              three terrible deals already."
            </p>
            <div style={styles.testimonialAuthor}>
              <span style={styles.authorName}>Marcus T.</span>
              <span style={styles.authorRole}>Software Developer</span>
            </div>
          </div>
          <div style={styles.testimonial}>
            <p style={styles.testimonialText}>
              "The negotiation suggestions helped me get paid upfront instead of net-60.
              That alone paid for a year."
            </p>
            <div style={styles.testimonialAuthor}>
              <span style={styles.authorName}>Jennifer K.</span>
              <span style={styles.authorRole}>Marketing Consultant</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" style={styles.pricingSection}>
        <h2 style={styles.sectionTitle}>Simple, Transparent Pricing</h2>
        <p style={styles.sectionSubtitle}>Choose the plan that fits your contract volume</p>

        <div style={styles.pricingGrid}>
          {PRICING_TIERS.map((tier) => (
            <div
              key={tier.plan}
              style={{
                ...styles.pricingCard,
                ...(tier.highlighted ? styles.pricingCardHighlighted : {}),
              }}
            >
              {tier.highlighted && <div style={styles.popularBadge}>Most Popular</div>}
              <h3 style={styles.pricingName}>{tier.name}</h3>
              <div style={styles.pricingPrice}>
                <span style={styles.pricingCurrency}>$</span>
                <span style={styles.pricingAmount}>{tier.price}</span>
                <span style={styles.pricingPeriod}>/{tier.period}</span>
              </div>
              <p style={styles.pricingAnalyses}>{tier.analyses} analyses per month</p>
              <ul style={styles.pricingFeatures}>
                {tier.features.map((feature, i) => (
                  <li key={i} style={styles.pricingFeature}>
                    <svg style={styles.checkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                style={{
                  ...styles.pricingCta,
                  ...(tier.highlighted ? styles.pricingCtaHighlighted : {}),
                }}
                onClick={() => onPricingCta(tier)}
                disabled={checkingOut !== null}
              >
                {checkingOut === tier.plan ? 'Loading...' : tier.cta}
              </button>
            </div>
          ))}
        </div>

        <p style={styles.pricingNote}>
          All plans include a 7-day free trial. Cancel anytime.
        </p>
      </section>

      {/* FAQ Section */}
      <section id="faq" style={styles.faqSection}>
        <h2 style={styles.sectionTitle}>Frequently Asked Questions</h2>
        <div style={styles.faqList}>
          <details style={styles.faqItem}>
            <summary style={styles.faqQuestion}>How accurate is the analysis?</summary>
            <p style={styles.faqAnswer}>
              ClauseGuard uses advanced AI trained on thousands of contracts to identify
              risky clauses. While no tool is perfect, our system catches over 90% of
              common problematic provisions. We always recommend consulting a lawyer
              for high-stakes contracts.
            </p>
          </details>

          <details style={styles.faqItem}>
            <summary style={styles.faqQuestion}>Is my contract data safe?</summary>
            <p style={styles.faqAnswer}>
              Yes. Your contracts are encrypted in transit and processed securely.
              We don't store your documents after analysis unless you explicitly
              save them to your history. Your data is never shared or sold.
            </p>
          </details>

          <details style={styles.faqItem}>
            <summary style={styles.faqQuestion}>What file formats do you support?</summary>
            <p style={styles.faqAnswer}>
              We support PDF (.pdf), Microsoft Word (.docx), and plain text files.
              Scanned documents are supported but may have reduced accuracy depending
              on image quality.
            </p>
          </details>

          <details style={styles.faqItem}>
            <summary style={styles.faqQuestion}>Can I analyze contracts in languages other than English?</summary>
            <p style={styles.faqAnswer}>
              Currently, ClauseGuard is optimized for English-language contracts.
              Support for additional languages is on our roadmap.
            </p>
          </details>

          <details style={styles.faqItem}>
            <summary style={styles.faqQuestion}>What happens if I go over my monthly limit?</summary>
            <p style={styles.faqAnswer}>
              You can upgrade your plan at any time. If you reach your limit,
              you'll be prompted to upgrade or wait until your next billing cycle.
              We'll never charge you unexpectedly.
            </p>
          </details>
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.ctaSection}>
        <h2 style={styles.ctaTitle}>Stop Signing Bad Contracts</h2>
        <p style={styles.ctaSubtitle}>
          Join thousands of freelancers who protect themselves with ClauseGuard
        </p>
        <button style={styles.ctaButton} onClick={onScrollToDemo}>
          Analyze Your First Contract Free
        </button>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerBrand}>
            <span style={styles.footerLogo}>ClauseGuard</span>
            <p style={styles.footerTagline}>Protecting freelancers, one clause at a time.</p>
          </div>
          <div style={styles.footerLinks}>
            <div style={styles.footerColumn}>
              <h4 style={styles.footerHeading}>Product</h4>
              <a href="#features" style={styles.footerLink}>Features</a>
              <a href="#pricing" style={styles.footerLink}>Pricing</a>
              <a href="#faq" style={styles.footerLink}>FAQ</a>
            </div>
            <div style={styles.footerColumn}>
              <h4 style={styles.footerHeading}>Legal</h4>
              <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('privacy'); }} style={styles.footerLink}>Privacy Policy</a>
              <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('terms'); }} style={styles.footerLink}>Terms of Service</a>
            </div>
            <div style={styles.footerColumn}>
              <h4 style={styles.footerHeading}>Contact</h4>
              <a href="mailto:support@clauseguard.com" style={styles.footerLink}>support@clauseguard.com</a>
            </div>
          </div>
        </div>
        <div style={styles.footerBottom}>
          <p>&copy; {new Date().getFullYear()} ClauseGuard. All rights reserved.</p>
        </div>
      </footer>
    </>
  );
}

// ============================================================================
// Dashboard Component
// ============================================================================

interface DashboardProps {
  analysis: Analysis | null;
  loading: boolean;
  error: string | null;
  dragActive: boolean;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBack: () => void;
  onErrorDismiss: () => void;
}

function Dashboard({
  analysis,
  loading,
  error,
  dragActive,
  onDrop,
  onDragOver,
  onDragLeave,
  onFileChange,
  onBack,
  onErrorDismiss,
}: DashboardProps) {
  return (
    <div style={styles.dashboardContainer}>
      <header style={styles.dashboardHeader}>
        <button style={styles.backButton} onClick={onBack}>
          <svg style={styles.backIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 style={styles.dashboardTitle}>ClauseGuard</h1>
        <div style={styles.dashboardSpacer} />
      </header>

      <main style={styles.dashboardMain}>
        <section style={styles.uploadSection}>
          <div
            style={{ ...styles.dropZone, ...(dragActive ? styles.dropZoneActive : {}) }}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
          >
            <svg style={styles.uploadIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            <p style={styles.dropText}>Analyze another contract</p>
            <p style={styles.dropSubtext}>Drag & drop or click to browse (PDF, DOCX)</p>
            <input type="file" accept=".pdf,.docx" onChange={onFileChange} style={styles.fileInput} />
          </div>
        </section>

        {loading && (
          <section style={styles.loadingSection}>
            <div style={styles.spinner} />
            <p style={styles.loadingText}>Analyzing your contract...</p>
          </section>
        )}

        {error && (
          <section style={styles.errorSection}>
            <p style={styles.errorText}>{error}</p>
            <button style={styles.retryButton} onClick={onErrorDismiss}>Try Again</button>
          </section>
        )}

        {analysis && !loading && (
          <section style={styles.resultsSection}>
            <SummaryCard summary={analysis.summary} />
            <ClausesList clauses={analysis.clauses} />
          </section>
        )}
      </main>
    </div>
  );
}

// ============================================================================
// Summary Card Component
// ============================================================================

function SummaryCard({ summary }: { summary: NonNullable<Analysis['summary']> }) {
  const riskColor = summary.high_risk_count > 0 ? '#dc2626' : summary.medium_risk_count > 0 ? '#f59e0b' : '#16a34a';

  return (
    <div style={styles.summaryCard}>
      <div style={styles.summaryHeader}>
        <h2 style={styles.summaryTitle}>Analysis Complete</h2>
        <div style={{ ...styles.riskBadge, backgroundColor: riskColor }}>
          {summary.high_risk_count > 0 ? 'High Risk' : summary.medium_risk_count > 0 ? 'Medium Risk' : 'Low Risk'}
        </div>
      </div>

      <div style={styles.summaryStats}>
        <div style={styles.statItem}>
          <span style={styles.statValue}>{summary.total_clauses}</span>
          <span style={styles.statLabel}>Clauses Found</span>
        </div>
        <div style={{ ...styles.statItem, color: '#dc2626' }}>
          <span style={styles.statValue}>{summary.high_risk_count}</span>
          <span style={styles.statLabel}>High Risk</span>
        </div>
        <div style={{ ...styles.statItem, color: '#f59e0b' }}>
          <span style={styles.statValue}>{summary.medium_risk_count}</span>
          <span style={styles.statLabel}>Medium Risk</span>
        </div>
        <div style={{ ...styles.statItem, color: '#16a34a' }}>
          <span style={styles.statValue}>{summary.low_risk_count}</span>
          <span style={styles.statLabel}>Low Risk</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Clauses List Component
// ============================================================================

function ClausesList({ clauses }: { clauses: Clause[] }) {
  return (
    <div style={styles.clausesList}>
      <h3 style={styles.clausesTitle}>Detailed Analysis</h3>
      {clauses.map((clause) => (
        <ClauseCard key={clause.id} clause={clause} />
      ))}
    </div>
  );
}

// ============================================================================
// Clause Card Component
// ============================================================================

function ClauseCard({ clause }: { clause: Clause }) {
  const [expanded, setExpanded] = useState(false);

  const riskColors = {
    high: { bg: '#fef2f2', border: '#dc2626', text: '#991b1b' },
    medium: { bg: '#fffbeb', border: '#f59e0b', text: '#92400e' },
    low: { bg: '#f0fdf4', border: '#16a34a', text: '#166534' },
    info: { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af' },
  };

  const colors = riskColors[clause.risk_level] || riskColors.low;
  const typeDisplay = clause.clause_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  return (
    <div style={{ ...styles.clauseCard, borderColor: colors.border, backgroundColor: colors.bg }}>
      <div style={styles.clauseHeader} onClick={() => setExpanded(!expanded)}>
        <div style={styles.clauseHeaderLeft}>
          <span style={{ ...styles.riskLevel, color: colors.text }}>{clause.risk_level.toUpperCase()}</span>
          <span style={styles.clauseType}>{typeDisplay}</span>
        </div>
        <span style={styles.expandIcon}>{expanded ? '−' : '+'}</span>
      </div>

      <p style={styles.clauseText}>"{clause.original_text}"</p>

      {expanded && (
        <div style={styles.clauseDetails}>
          <div style={styles.detailSection}>
            <h4 style={styles.detailTitle}>Why This Matters</h4>
            <p style={styles.detailText}>{clause.explanation}</p>
          </div>

          {clause.negotiation_suggestion && (
            <div style={styles.detailSection}>
              <h4 style={styles.detailTitle}>Suggested Action</h4>
              <p style={styles.detailText}>{clause.negotiation_suggestion}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Legal Page Component
// ============================================================================

interface LegalPageProps {
  title: string;
  onBack: () => void;
  children: React.ReactNode;
}

function LegalPage({ title, onBack, children }: LegalPageProps) {
  return (
    <div style={styles.legalContainer}>
      <header style={styles.legalHeader}>
        <button style={styles.backButton} onClick={onBack}>
          <svg style={styles.backIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 style={styles.legalTitle}>{title}</h1>
        <div style={styles.dashboardSpacer} />
      </header>
      <main style={styles.legalContent}>
        {children}
      </main>
    </div>
  );
}

// ============================================================================
// Privacy Policy Content
// ============================================================================

function PrivacyPolicyContent() {
  return (
    <div style={styles.legalText}>
      <p style={styles.legalP}><strong>Last Updated:</strong> February 2026</p>

      <h2 style={styles.legalH2}>1. Introduction</h2>
      <p style={styles.legalP}>
        ClauseGuard ("we," "our," or "us") respects your privacy and is committed to protecting
        your personal data. This privacy policy explains how we collect, use, and safeguard your
        information when you use our contract analysis service.
      </p>

      <h2 style={styles.legalH2}>2. Information We Collect</h2>
      <h3 style={styles.legalH3}>Account Information</h3>
      <p style={styles.legalP}>We collect:</p>
      <ul style={styles.legalUl}>
        <li style={styles.legalLi}>Email address (for account creation and communication)</li>
        <li style={styles.legalLi}>Payment information (processed securely by Stripe; we don't store card details)</li>
        <li style={styles.legalLi}>Usage data (number of analyses performed, subscription tier)</li>
      </ul>

      <h3 style={styles.legalH3}>Contract Documents</h3>
      <p style={styles.legalP}>
        When you upload a contract for analysis, we temporarily process the document to extract
        text and analyze it using AI. Documents are processed in memory and are not permanently
        stored on our servers unless you explicitly save them to your analysis history.
      </p>

      <h2 style={styles.legalH2}>3. How We Use Your Information</h2>
      <p style={styles.legalP}>We use your information to:</p>
      <ul style={styles.legalUl}>
        <li style={styles.legalLi}>Provide contract analysis services</li>
        <li style={styles.legalLi}>Process payments and manage your subscription</li>
        <li style={styles.legalLi}>Send service-related communications</li>
        <li style={styles.legalLi}>Improve our AI analysis accuracy (using anonymized patterns)</li>
        <li style={styles.legalLi}>Prevent fraud and abuse</li>
      </ul>

      <h2 style={styles.legalH2}>4. Data Security</h2>
      <p style={styles.legalP}>
        We implement industry-standard security measures including encryption in transit (TLS)
        and at rest. Your data is processed on Cloudflare's secure infrastructure with
        enterprise-grade security controls.
      </p>

      <h2 style={styles.legalH2}>5. Data Retention</h2>
      <ul style={styles.legalUl}>
        <li style={styles.legalLi}><strong>Account data:</strong> Retained until account deletion</li>
        <li style={styles.legalLi}><strong>Analysis history:</strong> Retained until you delete it or close your account</li>
        <li style={styles.legalLi}><strong>Uploaded documents:</strong> Not permanently stored unless saved to history</li>
      </ul>

      <h2 style={styles.legalH2}>6. Your Rights</h2>
      <p style={styles.legalP}>You have the right to:</p>
      <ul style={styles.legalUl}>
        <li style={styles.legalLi}>Access your personal data</li>
        <li style={styles.legalLi}>Correct inaccurate data</li>
        <li style={styles.legalLi}>Request deletion of your data</li>
        <li style={styles.legalLi}>Export your data</li>
        <li style={styles.legalLi}>Opt out of marketing communications</li>
      </ul>

      <h2 style={styles.legalH2}>7. Third-Party Services</h2>
      <p style={styles.legalP}>We use these third-party services:</p>
      <ul style={styles.legalUl}>
        <li style={styles.legalLi}><strong>Stripe:</strong> Payment processing (see stripe.com/privacy)</li>
        <li style={styles.legalLi}><strong>Cloudflare:</strong> Hosting and AI processing (see cloudflare.com/privacypolicy)</li>
      </ul>

      <h2 style={styles.legalH2}>8. Contact Us</h2>
      <p style={styles.legalP}>
        For privacy-related questions or to exercise your rights, contact us at:
        <br /><br />
        Email: privacy@clauseguard.com
      </p>
    </div>
  );
}

// ============================================================================
// Terms of Service Content
// ============================================================================

function TermsOfServiceContent() {
  return (
    <div style={styles.legalText}>
      <p style={styles.legalP}><strong>Last Updated:</strong> February 2026</p>

      <h2 style={styles.legalH2}>1. Acceptance of Terms</h2>
      <p style={styles.legalP}>
        By accessing or using ClauseGuard, you agree to be bound by these Terms of Service
        and all applicable laws and regulations. If you do not agree with any of these terms,
        you are prohibited from using this service.
      </p>

      <h2 style={styles.legalH2}>2. Description of Service</h2>
      <p style={styles.legalP}>
        ClauseGuard provides AI-powered contract analysis to help identify potentially risky
        clauses in legal documents. Our service is designed for informational purposes only
        and does not constitute legal advice.
      </p>

      <h2 style={styles.legalH2}>3. Disclaimer of Legal Advice</h2>
      <p style={styles.legalP}>
        <strong>IMPORTANT:</strong> ClauseGuard is not a law firm and does not provide legal advice.
        The analysis and suggestions provided are for informational purposes only and should not
        be relied upon as legal counsel. Always consult a qualified attorney for legal matters.
      </p>

      <h2 style={styles.legalH2}>4. User Accounts</h2>
      <p style={styles.legalP}>You are responsible for:</p>
      <ul style={styles.legalUl}>
        <li style={styles.legalLi}>Maintaining the confidentiality of your account credentials</li>
        <li style={styles.legalLi}>All activities that occur under your account</li>
        <li style={styles.legalLi}>Ensuring your use complies with applicable laws</li>
      </ul>

      <h2 style={styles.legalH2}>5. Subscription and Payments</h2>
      <ul style={styles.legalUl}>
        <li style={styles.legalLi}>Subscriptions are billed monthly or annually in advance</li>
        <li style={styles.legalLi}>All payments are non-refundable except as required by law</li>
        <li style={styles.legalLi}>You may cancel at any time; service continues until the end of the billing period</li>
        <li style={styles.legalLi}>We reserve the right to change pricing with 30 days notice</li>
      </ul>

      <h2 style={styles.legalH2}>6. Usage Limits</h2>
      <p style={styles.legalP}>
        Each subscription tier includes a monthly limit on contract analyses. If you exceed your
        limit, you may upgrade your plan or wait until the next billing cycle.
      </p>

      <h2 style={styles.legalH2}>7. Acceptable Use</h2>
      <p style={styles.legalP}>You agree not to:</p>
      <ul style={styles.legalUl}>
        <li style={styles.legalLi}>Use the service for illegal purposes</li>
        <li style={styles.legalLi}>Analyze documents you don't have permission to analyze</li>
        <li style={styles.legalLi}>Attempt to reverse engineer or extract our AI models</li>
        <li style={styles.legalLi}>Share your account credentials with others</li>
        <li style={styles.legalLi}>Use automated tools to abuse the service</li>
      </ul>

      <h2 style={styles.legalH2}>8. Intellectual Property</h2>
      <p style={styles.legalP}>
        ClauseGuard and its original content, features, and functionality are owned by us and
        protected by international copyright, trademark, and other intellectual property laws.
      </p>

      <h2 style={styles.legalH2}>9. Limitation of Liability</h2>
      <p style={styles.legalP}>
        To the maximum extent permitted by law, ClauseGuard shall not be liable for any
        indirect, incidental, special, consequential, or punitive damages, including without
        limitation, loss of profits, data, or other intangible losses.
      </p>

      <h2 style={styles.legalH2}>10. Indemnification</h2>
      <p style={styles.legalP}>
        You agree to indemnify and hold harmless ClauseGuard from any claims, damages, or
        expenses arising from your use of the service or violation of these terms.
      </p>

      <h2 style={styles.legalH2}>11. Termination</h2>
      <p style={styles.legalP}>
        We may terminate or suspend your account at any time for any reason, including violation
        of these terms. Upon termination, your right to use the service ceases immediately.
      </p>

      <h2 style={styles.legalH2}>12. Changes to Terms</h2>
      <p style={styles.legalP}>
        We reserve the right to modify these terms at any time. We will notify users of material
        changes via email or in-app notification. Continued use after changes constitutes acceptance.
      </p>

      <h2 style={styles.legalH2}>13. Governing Law</h2>
      <p style={styles.legalP}>
        These terms shall be governed by and construed in accordance with the laws of the
        jurisdiction in which ClauseGuard operates, without regard to conflict of law principles.
      </p>

      <h2 style={styles.legalH2}>14. Contact</h2>
      <p style={styles.legalP}>
        For questions about these terms, contact us at:
        <br /><br />
        Email: legal@clauseguard.com
      </p>
    </div>
  );
}

// ============================================================================
// Success Page Component
// ============================================================================

interface SuccessPageProps {
  onBack: () => void;
  onAnalyze: () => void;
}

function SuccessPage({ onBack, onAnalyze }: SuccessPageProps) {
  return (
    <div style={styles.successContainer}>
      <header style={styles.successHeader}>
        <button style={styles.backButton} onClick={onBack}>
          <svg style={styles.backIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 style={styles.legalTitle}>ClauseGuard</h1>
        <div style={styles.dashboardSpacer} />
      </header>
      <main style={styles.successContent}>
        <div style={styles.successCard}>
          <div style={styles.successIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <h2 style={styles.successTitle}>Payment Successful!</h2>
          <p style={styles.successText}>
            Thank you for subscribing to ClauseGuard. Your account is now active.
          </p>
          <p style={styles.successSubtext}>
            You can now analyze contracts and protect yourself from risky clauses.
          </p>
          <button style={styles.successCta} onClick={onAnalyze}>
            Analyze Your First Contract
          </button>
        </div>
      </main>
    </div>
  );
}

// ============================================================================
// Verify Email Page Component
// ============================================================================

interface VerifyEmailPageProps {
  onBack: () => void;
  onSuccess: () => void;
}

// Helper to clear URL params when navigating away
function clearUrlParams() {
  if (window.location.search || window.location.pathname !== '/') {
    window.history.replaceState({}, '', '/');
  }
}

function VerifyEmailPage({ onBack, onSuccess }: VerifyEmailPageProps) {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const handleBack = () => {
    clearUrlParams();
    onBack();
  };

  useEffect(() => {
    const verifyEmail = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');

      if (!token) {
        setStatus('error');
        setMessage('No verification token found. Please check your email link.');
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/auth/verify-email/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const result = await response.json() as { success: boolean; data?: { verified?: boolean; token?: string }; error?: { message?: string } };

        if (result.success && result.data?.verified) {
          // Store the new token if provided
          if (result.data.token) {
            localStorage.setItem('clauseguard_token', result.data.token);
          }
          setStatus('success');
          setMessage('Your email has been verified successfully!');
          // Redirect after 2 seconds
          setTimeout(() => {
            window.history.replaceState({}, '', '/');
            onSuccess();
          }, 2000);
        } else {
          setStatus('error');
          setMessage(result.error?.message || 'Verification failed. The link may have expired.');
        }
      } catch (err) {
        setStatus('error');
        setMessage('An error occurred during verification. Please try again.');
      }
    };

    verifyEmail();
  }, [onSuccess]);

  return (
    <div style={styles.successContainer}>
      <header style={styles.successHeader}>
        <button style={styles.backButton} onClick={handleBack}>
          <svg style={styles.backIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 style={styles.legalTitle}>ClauseGuard</h1>
        <div style={styles.dashboardSpacer} />
      </header>
      <main style={styles.successContent}>
        <div style={styles.successCard}>
          {status === 'loading' && (
            <>
              <div style={styles.spinner} />
              <h2 style={styles.successTitle}>Verifying Email...</h2>
              <p style={styles.successText}>Please wait while we verify your email address.</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div style={{ ...styles.successIcon, backgroundColor: '#dcfce7', color: '#16a34a' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <h2 style={styles.successTitle}>Email Verified!</h2>
              <p style={styles.successText}>{message}</p>
              <p style={styles.successSubtext}>Redirecting you to the dashboard...</p>
            </>
          )}
          {status === 'error' && (
            <>
              <div style={{ ...styles.successIcon, backgroundColor: '#fef2f2', color: '#dc2626' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M15 9l-6 6M9 9l6 6" />
                </svg>
              </div>
              <h2 style={styles.successTitle}>Verification Failed</h2>
              <p style={styles.successText}>{message}</p>
              <button style={styles.successCta} onClick={handleBack}>
                Go Back
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles: Record<string, React.CSSProperties> = {
  // Container
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: '#ffffff',
    color: '#111827',
  },

  // Navigation
  nav: {
    position: 'sticky',
    top: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(8px)',
    borderBottom: '1px solid #e5e7eb',
    zIndex: 50,
  },
  navContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1rem 1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  logoIcon: {
    width: '28px',
    height: '28px',
    color: '#3b82f6',
  },
  logoText: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#111827',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
  },
  navLink: {
    color: '#4b5563',
    textDecoration: 'none',
    fontSize: '0.9375rem',
    fontWeight: 500,
  },
  navCta: {
    padding: '0.5rem 1rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
  },

  // Hero Section
  hero: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '4rem 1.5rem',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '4rem',
    alignItems: 'center',
  },
  heroContent: {
    maxWidth: '560px',
  },
  heroTitle: {
    fontSize: '3rem',
    fontWeight: 800,
    lineHeight: 1.1,
    margin: 0,
    color: '#111827',
  },
  heroSubtitle: {
    fontSize: '1.25rem',
    color: '#4b5563',
    lineHeight: 1.6,
    marginTop: '1.5rem',
    marginBottom: '2rem',
  },
  heroCtas: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
  },
  primaryCta: {
    padding: '0.875rem 1.75rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  secondaryCta: {
    padding: '0.875rem 1.75rem',
    backgroundColor: 'transparent',
    color: '#374151',
    border: '2px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  trustBadges: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    fontSize: '0.875rem',
    color: '#6b7280',
  },
  trustBadge: {},
  trustDivider: {
    color: '#d1d5db',
  },
  heroVisual: {
    display: 'flex',
    justifyContent: 'center',
  },
  mockAnalysisCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '400px',
  },
  mockHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #e5e7eb',
  },
  mockTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#111827',
  },
  mockRisk: {
    padding: '0.25rem 0.75rem',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  mockClauses: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  mockClause: {
    padding: '0.75rem',
    borderLeft: '4px solid',
    backgroundColor: '#f9fafb',
    borderRadius: '0 6px 6px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.875rem',
    color: '#374151',
  },
  mockHigh: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    color: '#dc2626',
    backgroundColor: '#fef2f2',
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
  },
  mockMedium: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    color: '#f59e0b',
    backgroundColor: '#fffbeb',
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
  },

  // Demo Section
  demoSection: {
    backgroundColor: '#f9fafb',
    padding: '4rem 1.5rem',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    textAlign: 'center',
    margin: '0 0 0.5rem 0',
    color: '#111827',
  },
  sectionSubtitle: {
    fontSize: '1.125rem',
    color: '#6b7280',
    textAlign: 'center',
    margin: '0 0 2rem 0',
  },

  // Upload Zone
  uploadSection: {
    marginBottom: '2rem',
    maxWidth: '500px',
    margin: '0 auto 2rem',
  },
  dropZone: {
    position: 'relative',
    border: '2px dashed #d1d5db',
    borderRadius: '12px',
    padding: '3rem 2rem',
    textAlign: 'center',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  dropZoneActive: {
    borderColor: '#3b82f6',
    backgroundColor: '#eff6ff',
  },
  uploadIcon: {
    width: '48px',
    height: '48px',
    margin: '0 auto 1rem',
    color: '#9ca3af',
  },
  dropText: {
    fontSize: '1.125rem',
    fontWeight: 500,
    marginBottom: '0.5rem',
    margin: '0 0 0.5rem 0',
  },
  dropSubtext: {
    fontSize: '0.875rem',
    color: '#6b7280',
    margin: 0,
  },
  fileInput: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
  },
  loadingDemo: {
    textAlign: 'center',
    padding: '2rem',
  },
  errorDemo: {
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    maxWidth: '500px',
    margin: '1rem auto 0',
  },

  // Features Section
  featuresSection: {
    padding: '5rem 1.5rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '2rem',
    marginTop: '3rem',
  },
  featureCard: {
    padding: '2rem',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    textAlign: 'center',
  },
  featureIcon: {
    width: '48px',
    height: '48px',
    margin: '0 auto 1rem',
    padding: '0.75rem',
    backgroundColor: '#dbeafe',
    borderRadius: '12px',
    color: '#3b82f6',
  },
  featureTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    margin: '0 0 0.75rem 0',
    color: '#111827',
  },
  featureDesc: {
    fontSize: '0.9375rem',
    color: '#4b5563',
    lineHeight: 1.6,
    margin: 0,
  },

  // Social Proof
  socialProof: {
    backgroundColor: '#f9fafb',
    padding: '5rem 1.5rem',
  },
  testimonials: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '2rem',
    maxWidth: '1200px',
    margin: '3rem auto 0',
  },
  testimonial: {
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  testimonialText: {
    fontSize: '1rem',
    lineHeight: 1.7,
    color: '#374151',
    margin: '0 0 1.5rem 0',
    fontStyle: 'italic',
  },
  testimonialAuthor: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  authorName: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#111827',
  },
  authorRole: {
    fontSize: '0.875rem',
    color: '#6b7280',
  },

  // Pricing Section
  pricingSection: {
    padding: '5rem 1.5rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1.5rem',
    marginTop: '3rem',
  },
  pricingCard: {
    position: 'relative',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '2rem',
    textAlign: 'center',
  },
  pricingCardHighlighted: {
    border: '2px solid #3b82f6',
    boxShadow: '0 10px 30px rgba(59, 130, 246, 0.15)',
  },
  popularBadge: {
    position: 'absolute',
    top: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '0.25rem 1rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  pricingName: {
    fontSize: '1.25rem',
    fontWeight: 600,
    margin: '0 0 1rem 0',
    color: '#111827',
  },
  pricingPrice: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'baseline',
    marginBottom: '0.5rem',
  },
  pricingCurrency: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#111827',
  },
  pricingAmount: {
    fontSize: '3rem',
    fontWeight: 700,
    color: '#111827',
  },
  pricingPeriod: {
    fontSize: '1rem',
    color: '#6b7280',
  },
  pricingAnalyses: {
    fontSize: '0.9375rem',
    color: '#6b7280',
    marginBottom: '1.5rem',
    marginTop: 0,
  },
  pricingFeatures: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 1.5rem 0',
    textAlign: 'left',
  },
  pricingFeature: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.5rem 0',
    fontSize: '0.9375rem',
    color: '#374151',
  },
  checkIcon: {
    width: '18px',
    height: '18px',
    flexShrink: 0,
    color: '#16a34a',
  },
  pricingCta: {
    width: '100%',
    padding: '0.875rem',
    backgroundColor: '#111827',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  pricingCtaHighlighted: {
    backgroundColor: '#3b82f6',
  },
  pricingNote: {
    textAlign: 'center',
    fontSize: '0.875rem',
    color: '#6b7280',
    marginTop: '2rem',
    margin: '2rem 0 0 0',
  },

  // FAQ Section
  faqSection: {
    backgroundColor: '#f9fafb',
    padding: '5rem 1.5rem',
  },
  faqList: {
    maxWidth: '700px',
    margin: '3rem auto 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  faqItem: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '1.25rem',
    cursor: 'pointer',
    border: '1px solid #e5e7eb',
  },
  faqQuestion: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
    listStyle: 'none',
  },
  faqAnswer: {
    fontSize: '0.9375rem',
    color: '#4b5563',
    lineHeight: 1.6,
    margin: '1rem 0 0 0',
  },

  // CTA Section
  ctaSection: {
    backgroundColor: '#111827',
    padding: '5rem 1.5rem',
    textAlign: 'center',
  },
  ctaTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'white',
    margin: '0 0 1rem 0',
  },
  ctaSubtitle: {
    fontSize: '1.125rem',
    color: '#9ca3af',
    margin: '0 0 2rem 0',
  },
  ctaButton: {
    padding: '1rem 2rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },

  // Footer
  footer: {
    backgroundColor: '#111827',
    borderTop: '1px solid #374151',
    padding: '3rem 1.5rem 1.5rem',
  },
  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '2rem',
    borderBottom: '1px solid #374151',
  },
  footerBrand: {},
  footerLogo: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'white',
  },
  footerTagline: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginTop: '0.5rem',
    margin: '0.5rem 0 0 0',
  },
  footerLinks: {
    display: 'flex',
    gap: '4rem',
  },
  footerColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  footerHeading: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'white',
    margin: '0 0 0.5rem 0',
  },
  footerLink: {
    fontSize: '0.875rem',
    color: '#9ca3af',
    textDecoration: 'none',
  },
  footerBottom: {
    textAlign: 'center',
    paddingTop: '1.5rem',
  },
  footerBottomText: {
    fontSize: '0.875rem',
    color: '#6b7280',
    margin: 0,
  },

  // Dashboard
  dashboardContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f9fafb',
  },
  dashboardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#374151',
  },
  backIcon: {
    width: '16px',
    height: '16px',
  },
  dashboardTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    margin: 0,
    color: '#111827',
  },
  dashboardSpacer: {
    width: '80px',
  },
  dashboardMain: {
    flex: 1,
    maxWidth: '800px',
    width: '100%',
    margin: '0 auto',
    padding: '2rem 1rem',
  },

  // Loading
  loadingSection: {
    textAlign: 'center',
    padding: '3rem',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e5e7eb',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 1rem',
  },
  loadingText: {
    fontSize: '1rem',
    color: '#6b7280',
    margin: 0,
  },

  // Error
  errorSection: {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    border: '1px solid #fecaca',
  },
  errorText: {
    color: '#dc2626',
    marginBottom: '1rem',
    margin: '0 0 1rem 0',
  },
  retryButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },

  // Results
  resultsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  summaryCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  summaryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  summaryTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    margin: 0,
  },
  riskBadge: {
    padding: '0.5rem 1rem',
    borderRadius: '9999px',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  summaryStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1rem',
  },
  statItem: {
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  statValue: {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: 700,
  },
  statLabel: {
    display: 'block',
    fontSize: '0.75rem',
    color: '#6b7280',
    marginTop: '0.25rem',
  },

  // Clauses
  clausesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  clausesTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
    margin: '0 0 0.5rem 0',
  },
  clauseCard: {
    borderRadius: '8px',
    border: '2px solid',
    padding: '1rem',
    transition: 'all 0.2s ease',
  },
  clauseHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: '0.75rem',
  },
  clauseHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  riskLevel: {
    fontSize: '0.75rem',
    fontWeight: 700,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    backgroundColor: 'rgba(0,0,0,0.1)',
  },
  clauseType: {
    fontSize: '0.9375rem',
    fontWeight: 600,
  },
  expandIcon: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#6b7280',
  },
  clauseText: {
    fontSize: '0.875rem',
    fontStyle: 'italic',
    color: '#4b5563',
    lineHeight: 1.5,
    margin: 0,
    paddingBottom: '0.5rem',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
  },
  clauseDetails: {
    marginTop: '1rem',
  },
  detailSection: {
    marginBottom: '1rem',
  },
  detailTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '0.5rem',
    margin: '0 0 0.5rem 0',
  },
  detailText: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: '#4b5563',
    margin: 0,
  },

  // Legal Pages
  legalContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f9fafb',
  },
  legalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
  },
  legalTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    margin: 0,
    color: '#111827',
  },
  legalContent: {
    flex: 1,
    maxWidth: '800px',
    width: '100%',
    margin: '0 auto',
    padding: '2rem 1.5rem',
  },
  legalText: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    lineHeight: 1.7,
  },
  legalH2: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginTop: '2rem',
    marginBottom: '1rem',
    color: '#111827',
  },
  legalH3: {
    fontSize: '1rem',
    fontWeight: 600,
    marginTop: '1.5rem',
    marginBottom: '0.75rem',
    color: '#374151',
  },
  legalP: {
    marginBottom: '1rem',
    color: '#4b5563',
    lineHeight: 1.7,
  },
  legalUl: {
    marginBottom: '1rem',
    paddingLeft: '1.5rem',
    color: '#4b5563',
  },
  legalLi: {
    marginBottom: '0.5rem',
    lineHeight: 1.6,
  },

  // Success Page
  successContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f9fafb',
  },
  successHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
  },
  successContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  successCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '3rem',
    textAlign: 'center',
    maxWidth: '480px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto 1.5rem',
    padding: '1rem',
    backgroundColor: '#dcfce7',
    borderRadius: '50%',
    color: '#16a34a',
  },
  successTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 1rem 0',
  },
  successText: {
    fontSize: '1.125rem',
    color: '#374151',
    marginBottom: '0.5rem',
    lineHeight: 1.6,
  },
  successSubtext: {
    fontSize: '0.9375rem',
    color: '#6b7280',
    marginBottom: '2rem',
    lineHeight: 1.6,
  },
  successCta: {
    padding: '1rem 2rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
};
